// AGC: React hook for querying CONAB commodity prices
// AGC: Wraps the conabService API client with React state management
// AGC: Usage: const { data, loading, error, refetch } = useConabPrices({ dataset: 'semanal_municipio', product: 'soja' })

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getConabPrices,
  type ConabQueryParams,
  type ConabPriceResponse,
} from '../services/conabService'

// AGC: Hook return type
interface UseConabPricesReturn {
  data: ConabPriceResponse | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * AGC: Hook to fetch CONAB prices from the backend
 * AGC: Automatically refetches when params change
 * @param params - query parameters (dataset, product, uf, municipio, limit)
 * @returns { data, loading, error, refetch }
 */
export function useConabPrices(params: ConabQueryParams | null): UseConabPricesReturn {
  const [data, setData] = useState<ConabPriceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // AGC: Track request version to avoid stale responses
  const requestRef = useRef(0)

  const fetchData = useCallback(async () => {
    if (!params || !params.dataset) {
      setData(null)
      setError(null)
      return
    }

    const currentRequest = ++requestRef.current
    setLoading(true)
    setError(null)

    try {
      const result = await getConabPrices(params)
      // AGC: Only update if this is still the latest request
      if (currentRequest === requestRef.current) {
        setData(result)
        if (result.error) {
          setError(result.error)
        }
      }
    } catch (err) {
      if (currentRequest === requestRef.current) {
        const message = err instanceof Error ? err.message : 'Failed to fetch CONAB prices'
        setError(message)
        setData(null)
      }
    } finally {
      if (currentRequest === requestRef.current) {
        setLoading(false)
      }
    }
  }, [
    params?.dataset,
    params?.product,
    params?.uf,
    params?.municipio,
    params?.limit,
    params?.debug,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

export default useConabPrices
